/**
 * Which theme the app is painted in.
 *
 * Three states, not two. `system` is the default and follows the operating system, which is what most
 * people expect and what a device switching to night mode should do on its own. `light` and `dark` are
 * explicit choices that override it — a stated preference has to beat an inferred one, or the setting does
 * nothing for the person whose OS disagrees with them.
 */
export type ThemeChoice = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_KEY = 'eftc.theme';

export function isThemeChoice(v: unknown): v is ThemeChoice {
  return v === 'system' || v === 'light' || v === 'dark';
}

/** The stored choice, or `system` when nothing has been chosen or storage is unavailable. */
export function readThemeChoice(): ThemeChoice {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return isThemeChoice(v) ? v : 'system';
  } catch {
    // Private browsing, or storage disabled. Following the OS is the right fallback, not an error.
    return 'system';
  }
}

/** What `system` currently means. Defaults to dark when the query is unsupported — the original design. */
export function systemTheme(): ResolvedTheme {
  try {
    return window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
  } catch {
    return 'dark';
  }
}

export const resolveTheme = (choice: ThemeChoice): ResolvedTheme =>
  choice === 'system' ? systemTheme() : choice;

/**
 * Paint it.
 *
 * Sets `data-theme` on <html> and keeps `theme-color` in step, so the browser chrome on mobile matches the
 * page instead of leaving a dark bar above a light app.
 */
export function applyTheme(choice: ThemeChoice): ResolvedTheme {
  const resolved = resolveTheme(choice);
  const root = document.documentElement;
  root.setAttribute('data-theme', resolved);
  root.style.colorScheme = resolved;
  const meta = document.querySelector('meta[name="theme-color"]');
  if (meta) meta.setAttribute('content', resolved === 'light' ? '#f4f6f7' : '#0b0d0e');
  return resolved;
}

export function storeThemeChoice(choice: ThemeChoice): void {
  try {
    localStorage.setItem(THEME_KEY, choice);
  } catch { /* not being able to remember it is not a reason to refuse to apply it */ }
}
